import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import equipmentService from '../../../services/equipment';
import { categoryOf, type Equipment } from '../../../types/equipment';

interface Props {
  value: number | null;
  editable: boolean;
  /** Whether the node has a relay — without one there is nothing to count. */
  hasRelay: boolean;
  onChange: (equipmentId: number | null) => void;
}

/**
 * Which equipment record this pump IS.
 *
 * Linking is what gives the pump an hour meter that moves by itself: every
 * period the node's relay reports on is added to the machine's hours when it
 * ends (server side, relayRunService), and the machine's hours-based service
 * intervals count against those. Pumps are offered first; anything else on
 * the register stays choosable, because a generator or a transfer pump may be
 * catalogued under a type of its own.
 */
export default function NodeEquipmentPicker({ value, editable, hasRelay, onChange }: Props) {
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    equipmentService.list({ status: 'active' })
      .then(res => { if (res.success && res.data) setEquipment(res.data); })
      .catch(() => setFailed(true));
  }, []);

  const pumps = equipment.filter(e => categoryOf(e) === 'pump');
  const others = equipment.filter(e => categoryOf(e) !== 'pump');

  return (
    <div>
      <label htmlFor="node-equipment" className="block text-xs text-gray-600 mb-0.5">Equipment record</label>
      <select
        id="node-equipment"
        className="input"
        disabled={!editable}
        value={value ?? ''}
        onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
      >
        <option value="">— not linked —</option>
        {pumps.length > 0 && (
          <optgroup label="Pumps">
            {pumps.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </optgroup>
        )}
        {others.length > 0 && (
          <optgroup label="Other equipment">
            {others.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </optgroup>
        )}
      </select>
      <p className="text-xs text-gray-500 mt-1">
        {failed
          ? 'Could not load the equipment register.'
          : value == null
            ? <>Link the pump&apos;s equipment record and its hours count up from this node&apos;s relay, so its services fall due on their own. Not on the register yet? <Link to="/equipment/new" className="underline">Add it</Link>.</>
            : hasRelay
              ? 'Its hour meter counts up whenever this node’s relay is on.'
              : 'Choose a relay below — until then nothing is counted.'}
      </p>
    </div>
  );
}
